<script lang="ts">
// Types exportés pour usage par les consommateurs du composant
export type StepStatus = 'pending' | 'loading' | 'done' | 'error';

export interface LoadingStep {
    key: string;
    label: string;
    status: StepStatus;
}
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
    show: boolean;
    steps: LoadingStep[];
    /** Style de l'icône pour l'état "en cours" : 'pulse' (défaut) ou 'spinner' */
    activeIcon?: 'pulse' | 'spinner';
    /** URL de l'image utilisateur — remplace le ✓ sur les étapes terminées si fournie */
    userImage?: string;
}>();

const doneCount   = computed(() => props.steps.filter(s => s.status === 'done' || s.status === 'error').length);
const activeCount = computed(() => props.steps.filter(s => s.status === 'loading').length);
const progressPct = computed(() => {
    if (props.steps.length === 0) return 0;
    return Math.round(((doneCount.value + activeCount.value * 0.5) / props.steps.length) * 100);
});
</script>

<template>
    <div class="ls-overlay no-overlap" v-if="show">
        <div class="ls-content">
            <!-- Barre de progression + pourcentage -->
            <div class="ls-progress-bar-row">
                <div class="ls-progress-track">
                    <div class="ls-progress-fill" :style="{ width: progressPct + '%' }" />
                </div>
                <div class="ls-progress-pct">{{ progressPct }}&nbsp;%</div>
            </div>

            <!-- Liste des étapes -->
            <ul class="ls-steps">
                <li v-for="step in steps" :key="step.key"
                    :class="[step.status, { 'spinner-mode': activeIcon === 'spinner' }]">
                    <span class="ls-step-icon">
                        <!-- Spinner SVG (arc rotatif) pour le mode 'spinner' -->
                        <svg v-if="step.status === 'loading' && activeIcon === 'spinner'"
                            class="ls-spinner" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" fill="none"
                                stroke="currentColor" stroke-width="2.5"
                                stroke-dasharray="28.3 28.3" />
                        </svg>
                        <img v-else-if="step.status === 'done' && userImage"
                            :src="userImage" class="ls-user-img" alt="" />
                        <span v-else-if="step.status === 'done'">✓</span>
                        <span v-else-if="step.status === 'error'">✗</span>
                    </span>
                    <span class="ls-step-label">{{ step.label }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
/* ── Overlay ── */
.ls-overlay {
    position: absolute;
    inset: 0;
    background: rgba(248, 250, 255, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Évite de recouvrir le menu latéral (56 px) et le header (72 px) */
.no-overlap {
    top: 72px;
    left: 56px;
}

/* Contenu centré — même largeur que le startup loader */
.ls-content {
    width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ── Barre de progression ── */
.ls-progress-bar-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    margin-bottom: 1.25rem;
}

.ls-progress-track {
    flex: 1;
    height: 6px;
    background: #dde6f8;
    border-radius: 3px;
    overflow: hidden;
}

.ls-progress-fill {
    height: 100%;
    background: #3351a6;
    border-radius: 3px;
    transition: width 0.5s ease;
}

.ls-progress-pct {
    font-size: 1rem;
    color: #3351a6;
    font-weight: 700;
    min-width: 2.8rem;
    text-align: right;
    flex-shrink: 0;
}

/* ── Liste des étapes ── */
.ls-steps {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
    text-align: left;
}

.ls-steps li {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.35rem 0;
    font-size: 0.92rem;
    color: #bbb;
    transition: color 0.3s ease;
}

/* ── Icône circulaire (identique au startup loader) ── */
.ls-step-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: background 0.3s ease, border-color 0.3s ease;
}

/* Pending : cercle gris vide — styles par défaut ci-dessus */

/* Active : pulse bleu O2S */
.ls-steps li.loading {
    color: #3351a6;
    font-weight: 600;
}

.ls-steps li.loading .ls-step-icon {
    border-color: #3351a6;
    animation: ls-pulse-icon 1.2s ease-in-out infinite;
}

@keyframes ls-pulse-icon {
    0%, 100% { background-color: transparent; }
    50%       { background-color: rgba(51, 81, 166, 0.35); }
}

/* Terminé : vert plein */
.ls-steps li.done {
    color: #4CAF50;
}

.ls-steps li.done .ls-step-icon {
    background: #4CAF50;
    border-color: #4CAF50;
    color: #fff;
}

/* Terminé avec image utilisateur : pas de fond vert, l'image remplace l'icône */
.ls-steps li.done .ls-step-icon:has(.ls-user-img) {
    background: transparent;
    border-color: #4CAF50;
    overflow: hidden;
    padding: 0;
}

.ls-user-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    display: block;
}

/* Erreur : rouge plein */
.ls-steps li.error {
    color: #e53935;
}

.ls-steps li.error .ls-step-icon {
    background: #e53935;
    border-color: #e53935;
    color: #fff;
}

/* ── Mode spinner : remplace le pulse par un arc SVG rotatif ── */

/* Annule l'animation pulse et la couleur de bordure dynamique */
.ls-steps li.spinner-mode.loading .ls-step-icon {
    animation: none;
    border-color: #dde6f8;
    color: #3351a6;
}

.ls-spinner {
    width: 14px;
    height: 14px;
    animation: ls-spin 0.9s linear infinite;
}

@keyframes ls-spin {
    to { transform: rotate(360deg); }
}
</style>
